/**
 * THE LENS, as controls: validation, provenance and the room's tags. They
 * narrow the reports as well as the feed, so they live in two places — the
 * feed's rail, and the "+ Narrow" picker above a report — and both render
 * this, so the two can never offer different narrowings.
 *
 * Moved out of `FilterRail` unchanged.
 */
import { useState } from 'react';
import type { BoardroomDetail } from '../../types';
import { VERIFIED_OPTIONS, tagNegated, tagPicked, tagTerms, type SourcedFilter, type VerifiedFilter } from './filters';

/** More values than this and the dimension folds to ~1.5 rows until hovered. */
export const FOLD_AFTER = 6;

export function ValidationFacets({
  verified,
  onVerified,
  verifiedCounts = null,
  sourced = null,
  onSourced,
  sourcedCounts = null,
}: {
  verified: VerifiedFilter | null;
  onVerified: (v: VerifiedFilter | null) => void;
  /** Checked chips counted under bucket + tag; null = no counts shown. */
  verifiedCounts?: Record<VerifiedFilter, number> | null;
  /** Persona items with no receipt. One chip, beside validation: the same question, asked of provenance. */
  sourced?: SourcedFilter | null;
  onSourced?: (v: SourcedFilter | null) => void;
  sourcedCounts?: Record<SourcedFilter, number> | null;
}) {
  return (
    <>
    {/* ABOVE the buckets: this cuts across every kind, so it reads as a lens on
        the list below rather than as one more bucket. */}
    <div>
      <div className="px-2 mb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Validation
      </div>
      <div className="px-2 flex flex-wrap gap-1">
        {VERIFIED_OPTIONS.map(o => {
          const active = verified === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onVerified(active ? null : o.key)}
              className={`text-xs px-1.5 py-0.5 rounded-full border transition ${
                active
                  ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
              }`}
              data-testid={`verified-${o.key}`}
            >
              {o.label}
              {verifiedCounts && (
                <span className="text-gray-400"> {verifiedCounts[o.key] ?? 0}</span>
              )}
            </button>
          );
        })}
        {onSourced && (
          <button
            type="button"
            onClick={() => onSourced(sourced === 'UNSOURCED' ? null : 'UNSOURCED')}
            title="Persona items with no receipt: nothing on the board says where they came from"
            className={`text-xs px-1.5 py-0.5 rounded-full border transition ${
              sourced === 'UNSOURCED'
                ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
            }`}
            data-testid="sourced-UNSOURCED"
          >
            unsourced
            {sourcedCounts && <span className="text-gray-400"> {sourcedCounts.UNSOURCED ?? 0}</span>}
          </button>
        )}
      </div>
    </div>
    </>
  );
}

export function TagFacets({
  roomTagFacets,
  tagFacets = null,
  tags,
  onTag,
  onRemoveTagValue,
}: {
  /** Every value the room has, for the dimensions and the fallback counts. */
  roomTagFacets: BoardroomDetail['tag_facets'];
  /** Tag values counted under the other axes; null = use the room's totals. */
  tagFacets?: BoardroomDetail['tag_facets'] | null;
  /** Selected "DIM:value" keys: any of a dimension's, and every dimension. */
  tags: string[];
  /** Toggle one value on or off. */
  onTag: (key: string) => void;
  /** Delete one dimension:value from every item in the room (the hover ×). */
  onRemoveTagValue?: (dimension: string, value: string) => void;
}) {
  const dims = Object.keys(roomTagFacets).sort();
  // OPEN BY DEFAULT. A long dimension used to fold to two and a half rows with
  // a fade, and every group read as "collapsed" — the facets looked hidden on a
  // fresh room. Now every value shows; "less" folds a group you do not need.
  const [folded, setFolded] = useState<Set<string>>(() => new Set());
  const expanded = { has: (d: string) => !folded.has(d) };
  const toggleExpanded = (d: string) =>
    setFolded(prev => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  // Every picked value of each dimension: tags AND, so there can be several.
  const activeByDim = new Map<string, string[]>();
  for (const t of tagTerms(tags)) {
    const had = activeByDim.get(t.dimension) ?? [];
    activeByDim.set(t.dimension, [...had, ...t.values.filter(v => !had.includes(v))]);
  }
  /** Values for a dimension: faceted counts when we have them (a value the lens
   *  leaves at zero disappears, except the active ones so they can be deselected). */
  const valuesFor = (d: string) => {
    const src = tagFacets ? tagFacets[d] ?? [] : roomTagFacets[d];
    const active = activeByDim.get(d) ?? [];
    if (!active.length) return src;
    // The selected values go FIRST so a folded dimension never hides them.
    const hits = active.map(a => src.find(v => v.value === a) ?? { value: a, count: 0 });
    return [...hits, ...src.filter(v => !active.includes(v.value))];
  };

  return (
    <>
    {dims.length > 0 && (
      <div>
        <div className="px-2 mb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Tags
        </div>
        {dims.map(d => (
          <div key={d} className="group/dim mb-3">
            {/* The DIMENSION is the thing to read first — it is what makes
                "sales tech" an industry rather than a buyer — so it gets a
                heading of its own: darker, spaced, with a rule under it and
                the active value named beside it. */}
            {/* The underline gives way while the panel is open: its ring is the
                edge then, and two hairlines a pixel apart read as a glitch. */}
            <div
              className={`mx-2 mb-1 flex items-baseline justify-between gap-2 border-b pb-0.5 ${
                expanded.has(d)
                  ? 'border-transparent'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {valuesFor(d).length > FOLD_AFTER ? (
                /* THE WHOLE NAME IS THE EXPANDER — a click, not a hover: hover only
                   tints, so a brush across the rail never rearranges it. The
                   "all N / less" rides beside the name; the pinned value stays
                   on the right by itself. */
                <button
                  type="button"
                  onClick={() => toggleExpanded(d)}
                  aria-expanded={expanded.has(d)}
                  className="group/x flex items-baseline gap-1.5 min-w-0 text-left"
                  data-testid={`expand-${d}`}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 group-hover/x:text-indigo-700 dark:group-hover/x:text-indigo-300">
                    {d.toLowerCase().replace(/_/g, ' ')}
                  </span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 group-hover/x:text-indigo-600 dark:group-hover/x:text-indigo-300 whitespace-nowrap">
                    {expanded.has(d) ? '▾ less' : `▸ all ${valuesFor(d).length}`}
                  </span>
                </button>
              ) : (
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                  {d.toLowerCase().replace(/_/g, ' ')}
                </span>
              )}
              {activeByDim.get(d) && (
                <span
                  className="text-[11px] text-indigo-600 dark:text-indigo-300 truncate max-w-[9rem] shrink-0"
                  title={activeByDim.get(d)!.join(', ')}
                >
                  {activeByDim.get(d)!.join(', ')}
                </span>
              )}
            </div>
            {/* A LONG DIMENSION FOLDS. Past a handful of values the row shows about
                a row and a half with a fade; hovering tints it (this is something
                you can open), clicking the heading's "all N" opens it. The active
                value is pinned first so a fold never hides what is selected. */}
            <div
              className={`relative mx-2 px-1.5 py-1 rounded-md flex flex-wrap gap-1 overflow-hidden transition-[max-height] duration-200 ease-out ${
                valuesFor(d).length > FOLD_AFTER
                  ? expanded.has(d)
                    ? 'max-h-[60rem]'
                    : /* ~2.5 rows, so a fold is unmistakably a fold: two full
                         rows plus a cut-off third, not one row that might be
                         all there is. The FADE at the cut is what says there
                         is more, and the heading offers "all N" — a tinted,
                         ringed panel on hover said the same thing a third
                         time and left one dimension looking like a different
                         kind of control from its neighbours. */
                      'max-h-[4.4rem] rail-fold focus-within:max-h-[60rem]'
                  : ''
              }`}
              data-testid={`dim-${d}`}
            >
              {valuesFor(d).map(v => {
                const key = `${d}:${v.value}`;
                const active = tagPicked(tags, key);
                const not = active && tagNegated(tags, key);
                return (
                  <span
                    key={key}
                    className={`group inline-flex items-center rounded-full border text-xs transition ${
                      not
                        ? 'border-amber-300 bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100'
                        : active
                        ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <button type="button" onClick={() => onTag(key)} className="pl-1.5 pr-1 py-0.5">
                      {v.value} <span className="text-gray-400">{v.count}</span>
                    </button>
                    {onRemoveTagValue && (
                      /* HOVER ×: removes this value from EVERY item in the room.
                         Hidden until hover so the chip row stays a filter first. */
                      <button
                        type="button"
                        aria-label={`Remove tag ${d}:${v.value} from every item`}
                        title="Remove this tag from every item in the room"
                        onClick={e => {
                          e.stopPropagation();
                          onRemoveTagValue(d, v.value);
                        }}
                        className="icon-btn pr-1.5 py-0.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    )}
    </>
  );
}
